import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE DIALOG LAYER SLOT: WHAT MUST STAY OPERABLE WHILE A MODAL DIALOG IS OPEN.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * A modal `Dialog` (Radix, under `Modal` and `Drawer`) does three things to the rest of the
 * page, and each is correct: it traps focus inside the dialog, it marks everything else
 * `aria-hidden`, and it turns the body's pointer events off. The docked call bar
 * (`agent/SoftphonePanel.tsx`) is part of "everything else". So for the length of every open
 * drawer the agent's Answer, Mute and Hang up were covered by the scrim, unreachable by Tab and
 * unclickable — and an incoming call's focus move to Answer was pulled straight back into the
 * drawer by the trap (design round 2026-10-02, §1(b); confirmed in a browser and measured in
 * `SoftphonePanel.test.tsx` before this file existed). One correction to the audit, measured
 * there: the bar was NOT silent. The `aria-hidden` package exempts live regions, and the bar
 * is one — so a screen reader was told about the call and given no way to act on it, which
 * is worse than silence.
 *
 * ── THE FIX IS A SLOT, NOT AN EXEMPTION ─────────────────────────────────────
 * Two fixes were possible. Exempting the call bar from the dialog's blocking means fighting
 * all three mechanisms — Radix's `FocusScope` has no exemption API, `aria-hidden`'s
 * `hideOthers` hides whole siblings of the portal, and a pointer-down on an exempted surface
 * still counts as "outside" and dismisses the drawer. Each would need its own patch, and the
 * three patches would drift. The other fix is to put the call controls INSIDE the active
 * dialog's layer, where the trap, the hiding and the pointer rules already treat them as part
 * of the dialog. That is what this does: the shell hands a node (`<CallStrip />`) to the
 * provider, and the TOP-MOST open `DialogFrame` renders it at the foot of its panel.
 *
 * Top-most only (review, 2026-10-03): a Modal opened from a Drawer is two open dialogs, and a
 * strip in each was two Answer buttons and two live regions for one call — the lower one
 * inside the subtree Radix hides for the upper one, but still a second announcement. The
 * provider keeps the stack of open dialogs in order; a frame renders the slot only while it is
 * the last one registered.
 *
 * The design system stays ignorant of phones. It renders a `ReactNode`; what that node is, and
 * when it renders anything at all, is the shell's business. Outside a provider the slot is
 * `null`, which is why none of the page tests that mount a `Drawer` on its own change.
 *
 * ── AND THE STACK COUNTS OPEN DIALOGS ──────────────────────────────────────
 * The docked bar needs one fact: is a modal dialog open right now? If so, its own Answer
 * button must not take focus on an incoming call (the trap would pull focus back, and the
 * strip's Answer, which is inside the trap, is the one that should ring), and the bar itself
 * goes `inert` so it is not a second voice. `useOpenDialogCount()` is zero outside a provider.
 */

interface DialogLayerContextValue {
  slot: ReactNode;
  /** The open dialogs, oldest first. Each entry is the id a frame registered with. */
  stack: readonly number[];
  register: (id: number) => () => void;
}

const DialogLayerContext = createContext<DialogLayerContextValue | null>(null);

let nextId = 1;

export interface DialogLayerProviderProps {
  /**
   * Rendered at the foot of the top-most open `Modal`/`Drawer` panel. `null` renders nothing.
   * Pass a STABLE element (a module constant): the provider's value is memoised on it, and a
   * fresh element every render would re-render every open dialog on every shell render.
   */
  slot: ReactNode;
  children: ReactNode;
}

export function DialogLayerProvider({ slot, children }: DialogLayerProviderProps): ReactNode {
  const [stack, setStack] = useState<readonly number[]>([]);
  const register = useCallback((id: number): (() => void) => {
    setStack((s) => [...s, id]);
    return () => setStack((s) => s.filter((x) => x !== id));
  }, []);
  const value = useMemo(() => ({ slot, stack, register }), [slot, stack, register]);
  return <DialogLayerContext.Provider value={value}>{children}</DialogLayerContext.Provider>;
}

/**
 * @internal Called once by every `DialogFrame`, from inside `Dialog.Content`, so it registers
 * exactly while the dialog is open. Returns the slot to render — `null` unless this frame is
 * the top-most open dialog — and whether it is top-most, for anything else that must happen
 * once per stack (`DialogFrame` publishes its foot height for the toasts only when it is).
 */
export function useDialogLayer(): { slot: ReactNode; isTop: boolean } {
  const context = useContext(DialogLayerContext);
  const idRef = useRef(0);
  if (idRef.current === 0) idRef.current = nextId++;
  const id = idRef.current;
  const register = context?.register;
  useEffect(() => register?.(id), [register, id]);
  if (!context) return { slot: null, isTop: false };
  const isTop = context.stack.length > 0 && context.stack[context.stack.length - 1] === id;
  return { slot: isTop ? context.slot : null, isTop };
}

/** How many modal dialogs are open under the nearest provider. Zero outside one. */
export function useOpenDialogCount(): number {
  return useContext(DialogLayerContext)?.stack.length ?? 0;
}
